// frontend/src/graphql/groupBuying.js
// GraphQL queries and mutations for the group buying system

import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/client';

/**
 * Get current pricing for a product with group discount info
 */
export const GET_PRODUCT_PRICING = gql`
  query GetProductPricing($productId: ID!, $timeWindow: TimeWindow, $regionId: ID) {
    productPricing(productId: $productId, timeWindow: $timeWindow, regionId: $regionId) {
      currentPrice
      basePrice
      discount
      buyerCount
      closesAt
      timeRemaining
      hasExistingGroup
      groupId
      nextTier {
        buyersNeeded
        discount
        potentialPrice
      }
    }
  }
`;

/**
 * Get pricing for all time windows (for the selector)
 */
export const GET_ALL_WINDOW_PRICING = gql`
  query GetAllWindowPricing($productId: ID!, $regionId: ID) {
    allWindowPricing(productId: $productId, regionId: $regionId) {
      EXPRESS {
        currentPrice
        basePrice
        discount
        buyerCount
        closesAt
        nextTier {
          buyersNeeded
          discount
          potentialPrice
        }
      }
      ONE_DAY {
        currentPrice
        basePrice
        discount
        buyerCount
        closesAt
        nextTier {
          buyersNeeded
          discount
          potentialPrice
        }
      }
      THREE_DAY {
        currentPrice
        basePrice
        discount
        buyerCount
        closesAt
        nextTier {
          buyersNeeded
          discount
          potentialPrice
        }
      }
      SEVEN_DAY {
        currentPrice
        basePrice
        discount
        buyerCount
        closesAt
        nextTier {
          buyersNeeded
          discount
          potentialPrice
        }
      }
    }
  }
`;

/**
 * Get user's active buying groups
 */
export const GET_MY_BUYING_GROUPS = gql`
  query GetMyBuyingGroups {
    myBuyingGroups {
      id
      timeWindow
      status
      closesAt
      memberCount
      displayInfo {
        currentPrice
        basePrice
        discount
        buyerCount
        closesAt
        nextTier {
          buyersNeeded
          discount
          potentialPrice
        }
      }
      product {
        id
        name
        images {
          thumbnail
        }
      }
      region {
        id
        name
      }
    }
  }
`;

/**
 * Get available regions
 */
export const GET_REGIONS = gql`
  query GetRegions {
    regions {
      id
      slug
      name
      status
      deliveryEnabled
      pickupEnabled
    }
  }
`;

/**
 * Get pricing tiers configuration
 */
export const GET_PRICING_TIERS = gql`
  query GetPricingTiers {
    pricingTiers {
      minBuyers
      discount
      label
    }
  }
`;

/**
 * Create a purchase (with automatic group coordination)
 */
export const CREATE_PURCHASE_WITH_GROUP = gql`
  mutation CreatePurchase($input: CreatePurchaseInput!) {
    createPurchase(input: $input) {
      id
      status
      totalAmount
      timeWindow
      products {
        productId
        quantity
        priceAtPurchase
        totalPrice
      }
      buyingGroup {
        currentPrice
        basePrice
        discount
        buyerCount
        closesAt
        nextTier {
          buyersNeeded
          discount
          potentialPrice
        }
      }
      region {
        id
        name
      }
    }
  }
`;

/**
 * Commit to purchase after payment
 */
export const COMMIT_TO_PURCHASE = gql`
  mutation CommitToPurchase($purchaseId: ID!, $paymentIntentId: String!) {
    commitToPurchase(purchaseId: $purchaseId, paymentIntentId: $paymentIntentId) {
      id
      status
      confirmedAt
    }
  }
`;

/**
 * Switch time window for a purchase
 */
export const SWITCH_TIME_WINDOW = gql`
  mutation SwitchTimeWindow($purchaseId: ID!, $newTimeWindow: TimeWindow!) {
    switchTimeWindow(purchaseId: $purchaseId, newTimeWindow: $newTimeWindow) {
      success
      priceDifference
      fromGroup {
        currentPrice
        discount
        buyerCount
      }
      toGroup {
        currentPrice
        discount
        buyerCount
      }
    }
  }
`;

/**
 * Hook helpers for common operations
 */



/**
 * Hook to get product pricing with a specific time window
 */
export const useProductPricing = (productId, timeWindow = 'THREE_DAY', regionId = null) => {
  const { data, loading, error, refetch } = useQuery(GET_PRODUCT_PRICING, {
    variables: { productId, timeWindow, regionId },
    skip: !productId,
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000 // Refresh every 30 seconds to show live buyer count
  });
  
  return {
    pricing: data?.productPricing || null,
    loading,
    error,
    refetch
  };
};

/**
 * Hook to get all window pricing for a product
 */
export const useAllWindowPricing = (productId, regionId = null) => {
  const { data, loading, error, refetch } = useQuery(GET_ALL_WINDOW_PRICING, {
    variables: { productId, regionId },
    skip: !productId,
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000
  });
  
  return {
    pricing: data?.allWindowPricing || null,
    loading,
    error,
    refetch
  };
};

/**
 * Hook for creating a purchase with group coordination
 */
export const useCreatePurchase = () => {
  const [createPurchase, { data, loading, error }] = useMutation(CREATE_PURCHASE_WITH_GROUP);
  
  const create = async (products, timeWindow = 'THREE_DAY', options = {}) => {
    return createPurchase({
      variables: {
        input: {
          products,
          timeWindow,
          regionId: options.regionId,
          customHours: options.customHours,
          paymentMethod: options.paymentMethod || 'card',
          logistics: options.logistics
        }
      }
    });
  };
  
  return {
    createPurchase: create,
    purchase: data?.createPurchase,
    loading,
    error
  };
};